import { documentStatus } from '../../utils/fileTypes'

// Page body, then the folded corner drawn over it.
const PAGE = 'M8 2h21l13 13v39a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V6a4 4 0 0 1 4-4z'
const FOLD = 'M29 2v9a4 4 0 0 0 4 4h9'

// What's printed on the page hints at the kind of file: rows for a sheet, a
// landscape for an image, text lines for everything else.
function PageContent({ kind }) {
  if (kind === 'sheet') {
    return (
      <g className="fill-current">
        {[0, 1, 2, 3].map(row => [0, 1, 2].map(col => (
          <rect key={`${row}-${col}`} x={10 + col * 9} y={22 + row * 6.5} width="7.5" height="4.5" rx="0.8" opacity={row === 0 ? 0.45 : 0.18} />
        )))}
      </g>
    )
  }
  if (kind === 'image') {
    return (
      <g className="fill-current">
        <rect x="10" y="21" width="26" height="22" rx="2" opacity="0.12" />
        <circle cx="17" cy="27.5" r="2.6" opacity="0.45" />
        <path d="M10 41l8-8 5 5 4-4 9 9v0a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2z" opacity="0.4" />
      </g>
    )
  }
  return (
    <g className="fill-current">
      <rect x="10" y="22" width="18" height="2.4" rx="1.2" opacity="0.45" />
      {[28, 33, 38, 43].map((y, i) => (
        <rect key={y} x="10" y={y} width={i === 3 ? 15 : 26} height="2" rx="1" opacity="0.18" />
      ))}
    </g>
  )
}

/**
 * A small illustrated page for a file, tinted by its kind and stamped with
 * its extension. Lifts and tilts a touch when the surrounding `.group` is
 * hovered.
 */
export function FileGlyph({ file, className = 'h-16 w-14', animate = true }) {
  return (
    <div
      className={`relative flex-shrink-0 ${animate ? 'transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-safe:group-hover:-translate-y-1 motion-safe:group-hover:-rotate-[4deg]' : ''} ${className}`}
      aria-hidden="true"
    >
      <svg viewBox="0 0 46 60" className={`h-full w-full overflow-visible ${file.ink}`}>
        <path d={PAGE} className="fill-white stroke-gray-200 drop-shadow-[0_6px_10px_rgba(16,24,40,0.10)] dark:fill-gray-800 dark:stroke-gray-700" strokeWidth="1" />
        <path d={FOLD} className="fill-gray-100 stroke-gray-200 dark:fill-gray-700 dark:stroke-gray-600" strokeWidth="1" strokeLinejoin="round" />
        <PageContent kind={file.key} />
      </svg>
      <span className={`absolute -left-1.5 bottom-[14%] rounded-[5px] px-1.5 py-[1px] text-[9px] font-bold leading-[14px] tracking-wide text-white shadow-sm ring-1 ring-black/5 ${file.swatch}`}>
        {file.badge}
      </span>
    </div>
  )
}

// Compact square with the kind's icon, for list rows.
export function FileTile({ file, className = 'h-10 w-10' }) {
  const { Icon } = file
  return (
    <div className={`relative flex flex-shrink-0 items-center justify-center rounded-xl ring-1 ring-inset transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-safe:group-hover:scale-[1.06] ${file.tile} ${className}`} aria-hidden="true">
      <Icon className="h-[45%] w-[45%]" />
      <span className={`absolute -bottom-1 -right-1.5 rounded-[4px] px-1 text-[8px] font-bold leading-[13px] tracking-wide text-white shadow-sm ring-2 ring-white dark:ring-gray-900 ${file.swatch}`}>
        {file.badge}
      </span>
    </div>
  )
}

export function DocumentStatusChip({ status, displayName, className = '' }) {
  const config = documentStatus(status, displayName)
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${config.chip} ${className}`}>
      <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
        {config.live && <span className={`absolute inset-0 rounded-full opacity-60 motion-safe:animate-ping ${config.dot}`} />}
        <span className={`relative h-1.5 w-1.5 rounded-full ${config.dot}`} />
      </span>
      {config.label}
    </span>
  )
}
